import { DOCUMENT, NgIf, AsyncPipe } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component, OnInit, ChangeDetectionStrategy, OnDestroy, ViewChild, ElementRef, HostListener, Inject, AfterViewInit } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { ActivatedRoute, Data, Params, Router, UrlSegment } from '@angular/router';
import { SelectedCompanyService } from '@asamco/application-link/feature';
import { AsamcoAuthService, ignoredWebpackMessageTypes, MessageType, UserSession } from '@asamco/base';
import { getErrorSwal } from '@asamco/utils';
import { combineLatest, Observable, Subject, throwError } from 'rxjs';
import { catchError, map, shareReplay, switchMap, takeUntil } from 'rxjs/operators';
import Swal from 'sweetalert2';

interface SessionCreate {
	token: string;
	backendUrl: string;
	frontendUrl: string;
}
interface SessionData {
	appUrl: URL;
	userSession: {
		backend: Pick<UserSession['backend'], 'baseUrl' | 'odataBaseUrl' | 'token'>;
		loggedInBackends: Pick<UserSession['backend'], 'baseUrl' | 'odataBaseUrl' | 'token'>[];
	};
}

@Component({
    selector: 'app-shell-remote-iframe',
    templateUrl: './remote-iframe.component.html',
    styleUrls: ['./remote-iframe.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [NgIf, AsyncPipe],
})
export class RemoteIframeComponent implements OnInit, OnDestroy {
	private readonly destroy$ = new Subject<void>();

	private launchChecker;

	/**
	 * A combined observable of the route's url, params and queryParams.
	 * Can be used for convient access to anything route-related.
	 */
	private readonly routeData$ = combineLatest([this.route.url, this.route.queryParams, this.route.data]).pipe(
		map(([url, queryParams, data]) => ({
			url: url,
			queryParams: queryParams,
			data: data,
		}))
	);

	public readonly iframeSrc$: Observable<SafeResourceUrl> = this.routeData$.pipe(
		switchMap(routeData => {
			const backend = this.authService.userSession.loggedInBackends.find(b => b.name === routeData.data.backend);

			return this.selectedCompanyService.getCompanyIdObservable().pipe(
				map(companyId => {
					const userSession = {
						/** The primary and additional backends the user is logged in to */
						loggedInBackends: [backend],
						/** server settings of the server  */
						backend: backend,
						/** The authenticated user -- use oid: null here to bypass webapi-authentication-provider */
						user: {oid: null},
						companyId: companyId,
					};

					this.sessionData = {
						appUrl: new URL(backend.frontendUrl),
						userSession: userSession,
					};

					const initialAppUrl = this.getInitialAppUrl(
						routeData.data,
						this.sessionData.appUrl.href,
						routeData.url,
						routeData.queryParams
					);

					return this.sanitizer.bypassSecurityTrustResourceUrl(initialAppUrl.toString());
				})
			);
		}),
		catchError(error => {
			Swal.fire(Object.assign(getErrorSwal(error), { icon: 'error' })).finally(() =>
				this.router.navigateByUrl('managementportal/dashboard')
			);
			return throwError(() => error);
		}),
		shareReplay(1),
		takeUntil(this.destroy$)
	);

	private sessionData: SessionData;

	@ViewChild('innerAppIframe')
	public iframeRef: ElementRef<HTMLIFrameElement>;

	@HostListener('window:message', ['$event'])
	public onMessage(event: MessageEvent) {
		if (ignoredWebpackMessageTypes.includes(event.data.type)) {
			return;
		}

		if (event.data && event.data.type === MessageType.requestUserSession) {
			if (!this.iframeRef) {
				console.error('this.iframeRef is falsey while receiving requestUserSession message. This is unexpected.');
			}
			if (!this.iframeRef.nativeElement) {
				console.error('this.iframeRef.nativeElement is falsey while receiving requestUserSession message. This is unexpected.');
			}
			if (!this.sessionData) {
				console.error('this.sessionData is falsey while receiving requestUserSession message. This is unexpected.');
			}

			this.iframeRef.nativeElement.contentWindow.postMessage(
				{
					type: MessageType.userSession,
					payload: this.sessionData.userSession,
				},
				this.sessionData.appUrl.origin
			);
		}
	}

	constructor(
		@Inject(DOCUMENT) private document: Document,
		private http: HttpClient,
		private route: ActivatedRoute,
		private router: Router,
		private sanitizer: DomSanitizer,
		private selectedCompanyService: SelectedCompanyService,
		private authService: AsamcoAuthService
	) {}

	ngOnInit(): void {
		this.document.querySelectorAll('.main').forEach(mainEl => mainEl.classList.add('p-0'));

		this.launchChecker = setInterval(() => {
			const launcherFrame = this.document.querySelector<HTMLElement>('#launcher-frame');
			if(launcherFrame) {
				launcherFrame.style.minWidth = '90px';
				launcherFrame.style.maxWidth = '90px';
				clearInterval(this.launchChecker);
			}
		}, 50);
	}

	private getInitialAppUrl(data: Data, baseUrl: string, urlSegments: UrlSegment[], queryParams: Params): URL {
		const appPath = [data.app, ...Array.from(urlSegments)].join('/');

		// When we change from HashLocationStrategy (useHash: true) to something else,
		// we need to update this function (how we deal with hash AND the queryParams).
		const fullUrl = new URL(baseUrl);

		for (const [key, value] of Object.entries(queryParams)) {
			fullUrl.searchParams.append(key, value);
		}

		// the apps index might not be at the root, so append
		// fullUrl.pathname = `${fullUrl.pathname}#!/${appPath}`;
		const angularAppPath = `!/${appPath}${fullUrl.search}`;

		// reset the "real" query params
		fullUrl.search = '';

		fullUrl.hash = angularAppPath;

		return fullUrl;
	}

	ngOnDestroy(): void {
		this.document.querySelectorAll('.main').forEach(mainEl => mainEl.classList.remove('p-0'));

		this.destroy$.next();
		this.destroy$.complete();
	}
}
